/**
 * @lodex-pro/web-portal/routes/Webhooks — the operator admin's webhook endpoints (operator
 * webhooks PR 1, migration 0239; lx-coord/operator-webhooks-plan.md).
 *
 * Add an endpoint (an https URL and the events it wants), see its signing secret ONCE, pause or
 * resume it, rotate its secret (the old one keeps signing for an overlap) or remove it. The events
 * are sent from webhooks PR 2; the delivery log comes in PR 3.
 *
 * OPERATOR_ADMIN only (MANAGE_OPERATOR_WEBHOOKS). The nav entry is plan-locked by OP_WEBHOOKS; on
 * the page, a company without it sees its existing endpoints (to pause or remove) and the plan
 * message instead of "Add".
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import {
  OPERATOR_WEBHOOK_EVENT_TYPES,
  type OperatorWebhookEndpointView,
  type OperatorWebhookEventType,
  type OperatorWebhookListResponse,
  type OperatorWebhookSecretResponse,
} from '@lodex-pro/shared/validation/operator-webhooks';
import { usePortalMe } from '../hooks/useOperatorPortal';
import {
  useAddPortalWebhook,
  usePortalWebhooks,
  useRemovePortalWebhook,
  useRotatePortalWebhook,
  useTestPortalWebhook,
  useUpdatePortalWebhook,
  type PortalWebhookTestResult,
} from '../hooks/useOperatorWebhooks';
import { PortalDataError } from '../components/PortalDataError';
import { portalIsAdmin } from '../nav/portal-features';
import { localDateTime } from './ApiUsage';

// ── Words ────────────────────────────────────────────────────────────────────

export const EVENT_LABELS: Readonly<Record<OperatorWebhookEventType, string>> = {
  ACCEPTED: 'Accepted (the hauler assigned a driver)',
  EN_ROUTE: 'En route (the driver started)',
  ARRIVED_PICKUP: 'Arrived at pickup',
  PICKED_UP: 'Picked up',
  COMPLETED: 'Completed',
  TICKET_AVAILABLE: 'Ticket available (approved)',
  CANCELLED: 'Cancelled',
  VOIDED: 'Voided',
};

export const PLAN_OFF_MESSAGE = "Webhooks aren't on your company's plan. Contact Paelode.";
export const NOT_ADMIN_MESSAGE = "Only your company's portal admin can manage webhooks.";
export const SECRET_ONCE = "Copy it now. It won't be shown again.";

/** Why a URL was refused, from the API's field reason. */
export const URL_REFUSAL_TEXT: Readonly<Record<string, string>> = {
  NOT_A_URL: "That isn't a web address.",
  NOT_HTTPS: 'Use an https:// address.',
  PORT_NOT_ALLOWED: 'Use the standard https port (443): leave the port out of the address.',
  CREDENTIALS_IN_URL: "Don't put a user name or password in the address.",
  NO_HOST: 'The address has no host name.',
  NOT_A_PUBLIC_ADDRESS:
    'That address is private or internal. Use one that is reachable from the internet.',
  DNS_FAILED: "We couldn't look up that host name. Check it and try again.",
};

export const TEST_THROTTLED_TEXT = 'Wait 10 seconds between test events, then try again.';
export const SECRET_UNREADABLE_TEXT =
  "Not delivered: we couldn't read this endpoint's signing secret. Contact Paelode support.";

function limitMessage(limit: number | null): string {
  return limit === null
    ? 'Your company has every endpoint its plan allows.'
    : `Your plan allows ${String(limit)} webhook endpoint${limit === 1 ? '' : 's'}, and they are all set up. Remove one, or ask Paelode to raise the limit.`;
}

export function webhookErrorText(err: unknown, limit: number | null): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'OPERATOR_WEBHOOK_URL_REFUSED': {
        const reason = err.fields.find((f) => f.path === 'url')?.reason;
        return (reason !== undefined && URL_REFUSAL_TEXT[reason]) || URL_REFUSAL_TEXT['NOT_A_URL']!;
      }
      case 'OPERATOR_WEBHOOK_LIMIT_REACHED':
        return limitMessage(limit);
      case 'FEATURE_NOT_ENABLED':
        return PLAN_OFF_MESSAGE;
      case 'OPERATOR_WEBHOOK_NOT_FOUND':
        return 'That endpoint was removed. Reload the page.';
      case 'RATE_LIMIT_EXCEEDED':
        return TEST_THROTTLED_TEXT;
      default:
        break;
    }
  }
  return 'That didn’t work. Try again.';
}

// ── Styles (as the API keys page) ────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
  marginBottom: '16px',
};
const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)' };
const BUTTON: React.CSSProperties = {
  padding: '6px 14px',
  minHeight: '32px',
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 'var(--radius-pill)',
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON,
  background: 'var(--color-accent-blue)',
  border: '1px solid var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const DANGER: React.CSSProperties = {
  ...BUTTON,
  color: 'var(--color-danger)',
  border: '1px solid rgba(243, 139, 139, 0.4)',
};
const INPUT: React.CSSProperties = {
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  padding: '6px 8px',
  minHeight: '32px',
};
const ERROR: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '13px',
  margin: '8px 0 0',
};

// ── The secret, once ─────────────────────────────────────────────────────────

function SecretBox({
  result,
  heading,
  onDone,
}: {
  readonly result: OperatorWebhookSecretResponse;
  readonly heading: string;
  readonly onDone: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = (): void => {
    void (async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(result.secret);
        setCopied('copied');
      } catch {
        setCopied('failed');
      }
    })();
  };
  return (
    <section
      aria-label="New signing secret"
      style={{ ...CARD, borderColor: 'var(--color-accent-blue)' }}
    >
      <div style={{ marginBottom: '6px' }}>{heading}</div>
      <p style={{ fontSize: '14px', margin: '0 0 8px' }}>
        <strong>{SECRET_ONCE}</strong> Your system uses it to check that each webhook came from
        lodeX.
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="keep-case"
          aria-label="Signing secret"
          readOnly
          value={result.secret}
          onFocus={(e): void => e.target.select()}
          style={{ ...INPUT, fontFamily: 'monospace', flex: '1 1 320px', minWidth: 0 }}
        />
        <button type="button" onClick={copy} style={BUTTON}>
          Copy
        </button>
        <button type="button" onClick={onDone} style={PRIMARY}>
          Done
        </button>
      </div>
      {copied === 'copied' && (
        <p role="status" style={{ ...MUTED, fontSize: '13px', margin: '8px 0 0' }}>
          Copied.
        </p>
      )}
      {copied === 'failed' && (
        <p role="status" style={ERROR}>
          Could not copy. Select the secret and copy it yourself.
        </p>
      )}
      {result.endpoint.previousSecretExpiresAt !== null && (
        <p style={{ fontSize: '13px', margin: '8px 0 0' }}>
          The old secret keeps signing until{' '}
          {localDateTime(result.endpoint.previousSecretExpiresAt)}.
        </p>
      )}
    </section>
  );
}

// ── Add ──────────────────────────────────────────────────────────────────────

function EventPicker({
  value,
  onChange,
}: {
  readonly value: readonly OperatorWebhookEventType[];
  readonly onChange: (next: OperatorWebhookEventType[]) => void;
}): React.JSX.Element {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: '8px 0' }}>
      <legend style={{ fontSize: '13px', marginBottom: '4px' }}>Events to send</legend>
      {OPERATOR_WEBHOOK_EVENT_TYPES.map((t) => (
        <label key={t} style={{ display: 'block', fontSize: '14px', margin: '2px 0' }}>
          <input
            type="checkbox"
            checked={value.includes(t)}
            onChange={(e): void =>
              onChange(
                e.target.checked
                  ? OPERATOR_WEBHOOK_EVENT_TYPES.filter((x) => x === t || value.includes(x))
                  : value.filter((x) => x !== t),
              )
            }
          />{' '}
          {EVENT_LABELS[t]}
        </label>
      ))}
    </fieldset>
  );
}

function AddForm({
  limit,
  onCancel,
  onAdded,
}: {
  readonly limit: number | null;
  readonly onCancel: () => void;
  readonly onAdded: (r: OperatorWebhookSecretResponse) => void;
}): React.JSX.Element {
  const [url, setUrl] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [events, setEvents] = React.useState<OperatorWebhookEventType[]>([
    ...OPERATOR_WEBHOOK_EVENT_TYPES,
  ]);
  const [error, setError] = React.useState<string | null>(null);
  const add = useAddPortalWebhook();
  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    add.mutate(
      {
        url: url.trim(),
        ...(description.trim() !== '' ? { description: description.trim() } : {}),
        eventTypes: events,
      },
      {
        onSuccess: (r) => onAdded(r),
        onError: (err) => setError(webhookErrorText(err, limit)),
      },
    );
  };
  const ready = url.trim() !== '' && events.length > 0 && !add.isPending;
  return (
    <form aria-label="Add a webhook endpoint" onSubmit={submit} style={CARD}>
      <label style={{ display: 'block', fontSize: '13px' }}>
        URL
        <input
          className="keep-case"
          aria-label="URL"
          value={url}
          placeholder="https://your-system.example.com/lodex-webhooks"
          onChange={(e): void => setUrl(e.target.value)}
          style={{ ...INPUT, display: 'block', width: '100%', marginTop: '4px' }}
        />
      </label>
      <label style={{ display: 'block', fontSize: '13px', marginTop: '8px' }}>
        Description (optional)
        <input
          aria-label="Description"
          value={description}
          maxLength={200}
          onChange={(e): void => setDescription(e.target.value)}
          style={{ ...INPUT, display: 'block', width: '100%', marginTop: '4px' }}
        />
      </label>
      <EventPicker value={events} onChange={setEvents} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" disabled={!ready} style={PRIMARY}>
          Add endpoint
        </button>
        <button type="button" onClick={onCancel} style={BUTTON}>
          Cancel
        </button>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR}>
          {error}
        </p>
      )}
    </form>
  );
}

/** A test send's outcome in words. */
export function testResultText(r: PortalWebhookTestResult): string {
  if (r.delivered) {
    return `Delivered (HTTP ${String(r.httpStatus)}, ${String(r.durationMs)} ms).`;
  }
  const e = r.error ?? '';
  if (e.startsWith('URL_REFUSED:')) {
    return URL_REFUSAL_TEXT[e.slice('URL_REFUSED:'.length)] ?? URL_REFUSAL_TEXT['NOT_A_URL']!;
  }
  if (e.startsWith('HTTP_')) return `Not delivered: your system answered HTTP ${e.slice(5)}.`;
  if (e === 'REDIRECT_REFUSED') {
    return 'Not delivered: your system answered with a redirect. Use the final address.';
  }
  if (e === 'TIMEOUT') return 'Not delivered: no answer within 5 seconds.';
  if (e === 'SECRET_UNREADABLE') return SECRET_UNREADABLE_TEXT;
  return 'Not delivered: we could not connect to that address.';
}

// ── One endpoint's actions ───────────────────────────────────────────────────

const OVERLAPS: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 24, label: 'Keep the old secret for a day' },
  { hours: 168, label: 'Keep the old secret for a week' },
  { hours: 0, label: 'Stop the old secret now' },
];

function EndpointRow({
  endpoint,
  limit,
  onSecret,
  onNotice,
}: {
  readonly endpoint: OperatorWebhookEndpointView;
  readonly limit: number | null;
  readonly onSecret: (r: OperatorWebhookSecretResponse) => void;
  readonly onNotice: (text: string) => void;
}): React.JSX.Element {
  const update = useUpdatePortalWebhook(endpoint.id);
  const rotate = useRotatePortalWebhook(endpoint.id);
  const remove = useRemovePortalWebhook(endpoint.id);
  const test = useTestPortalWebhook(endpoint.id);
  const [tested, setTested] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'none' | 'rotate' | 'remove'>('none');
  const [overlap, setOverlap] = React.useState(24);
  const [error, setError] = React.useState<string | null>(null);
  const fail = (err: unknown): void => setError(webhookErrorText(err, limit));
  return (
    <li
      data-testid={`webhook-${endpoint.id}`}
      style={{ ...CARD, listStyle: 'none', opacity: endpoint.active ? 1 : 0.75 }}
    >
      <div style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>
        {endpoint.url}
      </div>
      {endpoint.description !== null && (
        <div style={{ fontSize: '13px', marginTop: '2px' }}>{endpoint.description}</div>
      )}
      <div style={{ ...MUTED, fontSize: '13px', marginTop: '4px' }}>
        {endpoint.active ? 'Active' : 'Paused'} · secret {endpoint.secretPrefix}… ·{' '}
        {endpoint.eventTypes.length === OPERATOR_WEBHOOK_EVENT_TYPES.length
          ? 'every event'
          : endpoint.eventTypes.map((t) => EVENT_LABELS[t].split(' (')[0]).join(', ')}
        {endpoint.previousSecretExpiresAt !== null &&
          ` · old secret signs until ${localDateTime(endpoint.previousSecretExpiresAt)}`}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={update.isPending}
          onClick={(): void =>
            update.mutate(
              { active: !endpoint.active },
              {
                onSuccess: () =>
                  onNotice(endpoint.active ? 'Endpoint paused.' : 'Endpoint resumed.'),
                onError: fail,
              },
            )
          }
          style={BUTTON}
        >
          {endpoint.active ? 'Pause' : 'Resume'}
        </button>
        <button type="button" onClick={(): void => setMode('rotate')} style={BUTTON}>
          New secret
        </button>
        <button
          type="button"
          disabled={test.isPending}
          onClick={(): void => {
            setTested(null);
            test.mutate(
              {},
              {
                onSuccess: (r) => setTested(testResultText(r.result)),
                onError: fail,
              },
            );
          }}
          style={BUTTON}
        >
          {test.isPending ? 'Sending…' : 'Send test event'}
        </button>
        <button type="button" onClick={(): void => setMode('remove')} style={DANGER}>
          Remove
        </button>
      </div>
      {mode === 'rotate' && (
        <div style={{ marginTop: '8px' }}>
          <label style={{ fontSize: '13px' }}>
            The old secret{' '}
            <select
              aria-label="Old secret"
              value={overlap}
              onChange={(e): void => setOverlap(Number(e.target.value))}
              style={INPUT}
            >
              {OVERLAPS.map((o) => (
                <option key={o.hours} value={o.hours}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>{' '}
          <button
            type="button"
            disabled={rotate.isPending}
            onClick={(): void =>
              rotate.mutate(
                { overlapHours: overlap },
                {
                  onSuccess: (r) => {
                    setMode('none');
                    onSecret(r);
                  },
                  onError: fail,
                },
              )
            }
            style={PRIMARY}
          >
            Make a new secret
          </button>{' '}
          <button type="button" onClick={(): void => setMode('none')} style={BUTTON}>
            Cancel
          </button>
        </div>
      )}
      {mode === 'remove' && (
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          No more events will be sent to this address.{' '}
          <button
            type="button"
            disabled={remove.isPending}
            onClick={(): void =>
              remove.mutate(
                {},
                {
                  onSuccess: () => onNotice('Endpoint removed.'),
                  onError: fail,
                },
              )
            }
            style={DANGER}
          >
            Remove it
          </button>{' '}
          <button type="button" onClick={(): void => setMode('none')} style={BUTTON}>
            Cancel
          </button>
        </div>
      )}
      {tested !== null && (
        <p
          role="status"
          data-testid={`webhook-test-${endpoint.id}`}
          style={{ fontSize: '13px', margin: '8px 0 0' }}
        >
          {tested}
        </p>
      )}
      {error !== null && (
        <p role="alert" style={ERROR}>
          {error}
        </p>
      )}
    </li>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────

type Panel =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'secret'; result: OperatorWebhookSecretResponse; heading: string };

function inUseText(d: OperatorWebhookListResponse): string {
  const n = d.endpoints.length;
  const of = d.limit === null ? '' : ` of ${String(d.limit)}`;
  return `${String(n)}${of} endpoint${n === 1 && d.limit === null ? '' : 's'} set up`;
}

function WebhooksManager(): React.JSX.Element {
  const { data, isLoading, isError, error } = usePortalWebhooks();
  const [panel, setPanel] = React.useState<Panel>({ kind: 'none' });
  const [notice, setNotice] = React.useState<string | null>(null);
  const atLimit = data !== undefined && data.limit !== null && data.endpoints.length >= data.limit;
  return (
    <>
      {isLoading && <p role="status">Loading webhooks…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load webhooks." />}
      {data !== undefined && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
              marginBottom: '16px',
            }}
          >
            <div data-testid="webhooks-in-use" style={{ fontSize: '14px' }}>
              {inUseText(data)}
            </div>
            {data.webhooksEnabled && panel.kind === 'none' && (
              <button
                type="button"
                disabled={atLimit}
                onClick={(): void => {
                  setNotice(null);
                  setPanel({ kind: 'add' });
                }}
                style={PRIMARY}
              >
                Add endpoint
              </button>
            )}
          </div>
          {!data.webhooksEnabled && (
            <p
              role="status"
              style={{
                padding: '12px 16px',
                background: 'rgba(123, 150, 232, 0.18)',
                color: 'var(--color-accent-blue-dark)',
                borderRadius: '12px',
                fontSize: '14px',
              }}
            >
              {PLAN_OFF_MESSAGE}
            </p>
          )}
          {data.webhooksEnabled && atLimit && panel.kind === 'none' && (
            <p role="status" style={{ ...MUTED, fontSize: '13px', marginTop: 0 }}>
              {limitMessage(data.limit)}
            </p>
          )}
          {notice !== null && (
            <p role="status" style={{ fontSize: '13px', marginTop: 0 }}>
              {notice}
            </p>
          )}
          {panel.kind === 'add' && (
            <AddForm
              limit={data.limit}
              onCancel={(): void => setPanel({ kind: 'none' })}
              onAdded={(result): void =>
                setPanel({
                  kind: 'secret',
                  result,
                  heading: `Endpoint added: ${result.endpoint.url}`,
                })
              }
            />
          )}
          {panel.kind === 'secret' && (
            <SecretBox
              result={panel.result}
              heading={panel.heading}
              onDone={(): void => setPanel({ kind: 'none' })}
            />
          )}
          {data.endpoints.length === 0 ? (
            <p style={{ ...MUTED, fontSize: '14px' }}>No webhook endpoints yet.</p>
          ) : (
            <ul style={{ padding: 0, margin: 0 }}>
              {data.endpoints.map((e) => (
                <EndpointRow
                  key={e.id}
                  endpoint={e}
                  limit={data.limit}
                  onNotice={setNotice}
                  onSecret={(result): void =>
                    setPanel({ kind: 'secret', result, heading: `New secret for ${e.url}` })
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}

export function Webhooks(): React.JSX.Element {
  const me = usePortalMe();
  const admin = portalIsAdmin(me.data);
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>Webhooks</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        lodeX sends your company&rsquo;s load events to your own systems as they happen: an HTTPS
        POST to each endpoint, signed with its secret.
      </p>
      {me.isError ? (
        <p role="alert">Could not load your account.</p>
      ) : me.data === undefined ? (
        <p role="status">Loading…</p>
      ) : admin ? (
        <WebhooksManager />
      ) : (
        <p role="status">{NOT_ADMIN_MESSAGE}</p>
      )}
    </main>
  );
}
